import { useEffect, useMemo, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Network } from '@capacitor/network';

import { chatApi, messageFrame, typingFrame, type MessageFramePayload } from '@/api/chat';
import { createChatSocket, type ChatSocket, type SocketLike } from '@/lib/chatSocket';
import { isOnline, onNetworkChange } from '@/lib/network';
import { isStoreBuild } from '@/lib/platform';

/**
 * Сокет чата для экрана переписки — ученика и куратора.
 *
 * Сам хук только связывает `lib/chatSocket.ts` с поводами переподключиться:
 *  - приложение вернулось из фона (в браузере — вкладка снова видна);
 *  - сеть появилась или сменилась (Wi-Fi ↔ сотовая).
 * Правила повтора, порядок «история → сокет» и разбор кадров — в `lib/chatSocket.ts`.
 */

type Options = {
  /** Диалог. Пока его нет — соединения нет. */
  roomId: string | null | undefined;
  /** Дозагрузить историю по REST и влить её в ленту. Зовётся перед каждым подключением и сразу после него. */
  catchUp: () => Promise<void>;
  /** Одно событие сокета: сообщение, «печатает», присутствие, отказ. */
  onEvent: (event: unknown) => void;
};

export type ChatSocketHandle = {
  /** Отправить сообщение по сокету. `false` — сокет не открыт, слать по REST. */
  send: (payload: MessageFramePayload) => boolean;
  /** Сообщить «печатает / перестал». Без открытого сокета — ничего. */
  typing: (roomId: string, isTyping: boolean) => void;
};

/** Отказ, после которого стучаться снова бессмысленно: права на чат нет или диалога нет. */
function isFatal(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 403 || status === 404;
}

export function useChatSocket({ roomId, catchUp, onEvent }: Options): ChatSocketHandle {
  const ctl = useRef<ChatSocket | null>(null);
  // Свежие обработчики без пересоздания соединения на каждую перерисовку экрана.
  const latest = useRef({ catchUp, onEvent });
  useEffect(() => {
    latest.current = { catchUp, onEvent };
  });

  useEffect(() => {
    if (!roomId) return;
    const socket = createChatSocket({
      open: () => chatApi.connectWS() as unknown as SocketLike | null,
      catchUp: () => latest.current.catchUp(),
      onEvent: (e) => latest.current.onEvent(e),
      isOnline,
      isFatal,
    });
    ctl.current = socket;
    socket.start();

    const removers: Array<() => void> = [];
    let gone = false;
    const keep = (p: Promise<{ remove: () => Promise<void> }>) => {
      void p
        .then((h) => {
          if (gone) void h.remove();
          else removers.push(() => void h.remove());
        })
        .catch(() => {});
    };

    if (isStoreBuild()) {
      keep(
        CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) socket.wake();
          else socket.hidden();
        }),
      );
      // ⚠️ Плагин, а не `onNetworkChange`: тот молчит, когда связь была и осталась (Wi-Fi → сотовая), а старое
      // соединение при этом мертво.
      keep(
        Network.addListener('networkStatusChange', (s) => {
          if (s.connected) socket.wake(true);
          else socket.offline();
        }),
      );
    } else {
      const onVisibility = () => {
        if (document.visibilityState === 'visible') socket.wake();
        else socket.hidden();
      };
      document.addEventListener('visibilitychange', onVisibility);
      removers.push(() => document.removeEventListener('visibilitychange', onVisibility));
      removers.push(
        onNetworkChange((online) => {
          if (online) socket.wake(true);
          else socket.offline();
        }),
      );
    }

    return () => {
      gone = true;
      removers.forEach((r) => r());
      socket.stop();
      ctl.current = null;
    };
  }, [roomId]);

  return useMemo<ChatSocketHandle>(
    () => ({
      send: (payload) => ctl.current?.send(messageFrame(payload)) ?? false,
      typing: (room, isTyping) => {
        ctl.current?.send(typingFrame(room, isTyping));
      },
    }),
    [],
  );
}
